import axios from 'axios'
import { useAxios } from '~/composable/useAxios'
// 신청 화면 '나만 보는 현황' 타입 (stores/dining.ts)
import type { DiningRegionMyStatusResponse } from '~/stores/dining'
import type ContentForPublic from '~/pages/app/content/content-for-public.dto'
import type Content from '~/pages/app/content/content.dto'
import type GatheringFeedDetailPublic from '~/pages/gathering/feed/gathering-feed-detail-public.dto'
import type GatheringFeedPublic from '~/pages/gathering/feed/gathering-feed-public.dto'
import type GatheringPublic from '~/pages/gathering/gathering-public.dto'
import type { RecommendedSchedule } from '~/pages/recommend/result/schedule.vue'
import type {
  ContentReviewItem,
  ContentReviewListInfo,
  GatheringInfoForNonMember,
  MemberList,
} from './types'

const getGatheringInfo = async (gatheringId: number) => {
  try {
    const [response1, response2] = await Promise.all([
      useAxios().get('/gathering/info-public', {
        params: {
          gatheringId,
          scheduleCount: 10,
          feedCount: 100,
          voteCount: 10,
        },
      }),
      useAxios().get('/gathering/member/list', { params: { gatheringId } }),
    ])
    const gatheringInfo = response1.data.value as GatheringInfoForNonMember
    gatheringInfo.scheduleList.forEach((schedule) => {
      schedule.createdAt = new Date(schedule.createdAt)
      schedule.when = new Date(schedule.when)
      schedule.closingAt = new Date(schedule.closingAt || schedule.when)
    })
    gatheringInfo.feedList.forEach((feed) => {
      feed.createdAt = new Date(feed.createdAt)
    })
    if (gatheringInfo.notice) {
      gatheringInfo.notice.createdAt = new Date(gatheringInfo.notice.createdAt)
    }
    gatheringInfo.createdAt = new Date(gatheringInfo.createdAt)
    return {
      result: response1.data.result as boolean,
      value: {
        ...(response1.data.value as GatheringInfoForNonMember),
        memberInfo: {
          list: (response2.data.list as MemberList).map((member) => {
            member.visitAt = new Date(member.visitAt || 0)
            return member
          }),
          capacity: response2.data.capacity as number,
        },
        suggestGatheringList: response1.data.value.suggestGatheringList as GatheringPublic[],
      },
    }
  } catch (err) {
    return {
      result: false,
      value: null,
    }
  }
}

const getEmoticonInfo = async (
  groupId: number
): Promise<{
  value: null | {
    groupId: number
    name: string
    price: number
    mainImage: string
    items: {
      orderId: number
      thumbImage: string
      image: string
    }[]
  }
}> => {
  try {
    const response = await useAxios().get('/inapp-purchase/emoticon/introduce', {
      params: { groupId },
    })
    return {
      value: response.data.value,
    }
  } catch (err) {
    console.error(err)
    return {
      value: null,
    }
  }
}

export type EmoticonStoreItem = {
  groupId: number
  name: string
  mainImage: string
  isOwned: boolean
  isNew: boolean
}

/** 이모티콘 스토어 목록 (oe-api 17.3.5). 로그인 필요. 최신순으로 온다 */
const getEmoticonStoreList = async (): Promise<{
  totalCount: number
  emoticons: EmoticonStoreItem[]
}> => {
  const response = await useAxios().get('/inapp-purchase/emoticon/store')
  return response.data.value
}

const getAllGatheringIds = async () => {
  try {
    const response = await useAxios().get('/gathering/all-ids')
    return {
      list: response.data.list as number[],
    }
  } catch (err) {
    return {
      list: [],
    }
  }
}

const getGatheringList = async (
  interest: string[],
  limit: number,
  offset: number,
  seed: number,
  ranking = false
) => {
  const path = ranking ? '/gathering/list-public/ranking' : '/gathering/list-public'
  const response = await useAxios().get(path, {
    params: { interest, limit, offset, seed },
  })
  return {
    list: response.data.list as GatheringPublic[],
    done: response.data.done as boolean,
    nextOffset: response.data.nextOffset as number | null,
  }
}

const getGatheringFeedList = async (limit: number, offset: number) => {
  const response = await useAxios().get('/gathering/feed/list-public', {
    params: { limit, offset },
  })
  let list = response.data.list as GatheringFeedPublic[]
  list.forEach((feed) => {
    feed.createdAt = new Date(feed.createdAt)
  })
  return {
    list: list,
    done: response.data.done as boolean,
    nextOffset: response.data.nextOffset as number | null,
  }
}

const getGatheringFeedDetail = async (id: number) => {
  const response = await useAxios().get('/gathering/feed/public', {
    params: { feedId: id },
  })
  let value = response.data.value as GatheringFeedDetailPublic
  if (value) {
    value.commentInfo.list.forEach((cmt) => {
      cmt.createdAt = new Date(cmt.createdAt)
    })
    value.createdAt = new Date(value.createdAt)
  }
  return {
    result: response.data.result as boolean,
    fail: (response.data.fail || undefined) as { message: '없음' },
    value: value as GatheringFeedDetailPublic,
  }
}

const getContentForPublic = async (contentId: number) => {
  const response = await useAxios().get('/content/for-public', {
    params: { contentId },
  })
  const value = response.data.value as ContentForPublic
  if (value) {
    value.scheduleAt = new Date(value.scheduleAt)
    value.paymentClosingAt = new Date(value.paymentClosingAt)
    value.memberInfo.imageList = value.memberInfo.imageList.map((image) => {
      if (image === '남성') {
        return '/images/il_user_male.svg'
      } else if (image === '여성') {
        return '/images/il_user_female.svg'
      } else {
        return image || '/images/profile_dummy.svg'
      }
    })
    value.host.image = value.host.image || '/images/profile_dummy.svg'
  }
  return {
    result: response.data.result as boolean,
    value: value,
  }
}

const getContent = async (id: number) => {
  const asyncData = await useAsyncData('content', async () => {
    const result = await useAxios().get('/content', {
      params: { id },
    })
    return result.data
  })
  const data = await asyncData.data.value!!
  const value = data.value as Content
  if (value) {
    value.scheduleAt = new Date(value.scheduleAt)
    value.paymentClosingAt = new Date(value.paymentClosingAt)
    value.memberInfo.imageList = value.memberInfo.imageList.map((image) => {
      if (image === '남성') {
        return '/images/il_user_male.svg'
      } else if (image === '여성') {
        return '/images/il_user_female.svg'
      } else {
        return image || '/images/profile_dummy.svg'
      }
    })
    value.host.image = value.host.image || '/images/profile_dummy.svg'
  }
  return {
    result: data.result as boolean,
    value: value,
  }
}

const getContentV2 = async (id: number) => {
  const response = await useAxios().get('/content', {
    params: { id },
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: Content
  }
  const value = data.value as Content
  if (value) {
    value.scheduleAt = new Date(value.scheduleAt)
    value.paymentClosingAt = new Date(value.paymentClosingAt)
    value.memberInfo.imageList = value.memberInfo.imageList.map((image) => {
      if (image === '남성') {
        return '/images/il_user_male.svg'
      } else if (image === '여성') {
        return '/images/il_user_female.svg'
      } else {
        return image || '/images/profile_dummy.svg'
      }
    })
    value.host.image = value.host.image || '/images/profile_dummy.svg'
  }
  return data
}

const getAccessToken = async (refreshToken: string) => {
  const response = await useAxios().post('/auth/issue-access-token', {
    refreshToken,
  })
  return response.data as {
    value: {
      result: boolean
      fail?: {
        message: '토큰만료' | '토큰에러'
      }
      accessToken?: string
    }
  }
}

const getUntransferredOEId = async (type: 'content' | 'gathering' | 'diningGroup', id: number) => {
  const response = await useAxios().get('/my/oe/untransferred-id', {
    params: {
      type,
      id,
    },
  })
  return response.data as {
    result: boolean
    fail?: {
      message: '기간마감' | '권한없음' | '이미전달됨'
    }
    value?: number
  }
}

const getContentWishUserList = async (contentId: number) => {
  const response = await useAxios().get('/content/wish/list', {
    params: {
      contentId,
    },
  })
  return response.data as {
    list: {
      id: number
      nickname: string
      image: string
    }[]
  }
}

const getContentWishUserListForPublic = async (contentId: number) => {
  const response = await useAxios().get('/content/wish/list/for-public', {
    params: {
      contentId,
    },
  })
  return response.data as {
    list: {
      id: number
      nickname: string
      image: string
    }[]
  }
}

const toggleContentReviewLike = async (reviewId: number) => {
  const response = await useAxios().put('/content/review/like', {
    reviewId,
  })
  return response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: boolean
  }
}

const toggleContentWish = async (contentId: number) => {
  const response = await useAxios().put('/content/wish', null, {
    params: {
      contentId,
    },
  })
  return response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: boolean
  }
}

const getContentReviewList = async (count: number, page: number, hostId?: number) => {
  const response = await useAxios().get('/content/review/list', {
    params: {
      count,
      page,
      hostId,
    },
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: ContentReviewListInfo
  }
  if (data.value) {
    data.value.reviewList.forEach((review) => {
      review.createdAt = new Date(review.createdAt)
    })
  }
  return data
}

const getContentReviewListForPublic = async (count: number, page: number, hostId?: number) => {
  const response = await useAxios().get('/content/review/list/for-public', {
    params: {
      count,
      page,
      hostId,
    },
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: ContentReviewListInfo
  }
  if (data.value) {
    data.value.reviewList.forEach((review) => {
      review.createdAt = new Date(review.createdAt)
    })
  }
  return data
}

const getContentReviewListItem = async (reviewId: number) => {
  const response = await useAxios().get('/content/review/list', {
    params: {
      reviewId,
    },
  })
  return response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: ContentReviewItem
  }
}

const cancelContent = async (contentId: number, reason: string) => {
  const response = await useAxios().put('/content/cancel', {
    contentId,
    reason,
  })
  return response.data as {
    result: boolean
    fail?: {
      message: '권한없음' | '날짜지남'
    }
  }
}

const getContentMemberList = async (contentId: number) => {
  const response = await useAxios().get('/content/member/list', {
    params: {
      contentId,
    },
  })
  return response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: {
      host: {
        id: number
        image: null | string
        name: string
      }
      amIHost: boolean
      info: {
        useMinimum: boolean
        useGender: boolean
        maxCapacity: number
        minCapacity: number | null
        maxMaleCapacity: number | null
        maxFemaleCapacity: number | null
        count: number
        maleCount: number
        femaleCount: number
      }
      list: {
        id: number
        nickname: string
        image: null | string
        friendCount: number
      }[]
    }
  }
}

const visitContent = async (contentId: number) => {
  await useAxios().post('/content/visit', null, {
    params: {
      contentId,
    },
  })
}

const getChatHealth = async () => {
  let version = 2
  if (version === 1) {
    return axios.get(`${useRuntimeConfig().public.CHAT_SERVER}/health`).catch((error) => {
      if (error.response?.status === 503) {
        throw error
      }
    })
  } else {
    while (true) {
      try {
        const response = await axios.get(`${useRuntimeConfig().public.CHAT_SERVER}/health`)
        return response // 성공하면 반환하고 종료
      } catch (error: any) {
        if (error.response?.status === 503) {
          // 503 에러면 1초 대기 후 재시도
          await new Promise((resolve) => setTimeout(resolve, 1000))
          continue
        }
        // 503이 아닌 다른 에러는 그대로 throw
        throw error
      }
    }
  }
}

const requestMyReferrerLink = async (
  source:
    | 'gatheringMember_normal_share'
    | 'gatheringGuest_normal_share'
    | 'gatheringMember_contact_share'
    | 'gatheringGuest_contact_share'
    | 'content_normal_share'
    | 'content_contact_share'
    | 'moretab_contact_share'
    | 'invite_gatheringWish'
    | 'invite_contentWish'
    | 'invite_frequentlyGathering'
    | 'invite_frequentlyContent'
    | 'invite_firstScheduleVisit'
    | 'invite_gatheringCreated'
    | 'invite_receiveGatheringMannerOe'
    | 'invite_receiveContentMannerOe'
    | 'invite_reviewGathering'
    | 'invite_reviewContent'
    | 'dining_normal_share',
  param: {
    contentId?: number
    gathering?: number
    diningOrderId?: string
    targetPhoneNumber?: string
    targetName?: string
  }
) => {
  const response = await useAxios().post('/my/referrer-link', {
    source,
    ...param,
  })
  return response.data as {
    value: {
      message: string
    }
  }
}

const getMyNudgeModal = async (
  type:
    | 'invite_gatheringWish'
    | 'invite_contentWish'
    | 'invite_frequentlyGathering'
    | 'invite_frequentlyContent'
    | 'invite_firstScheduleVisit'
    | 'invite_gatheringCreated'
    | 'invite_receiveGatheringMannerOe'
    | 'invite_receiveContentMannerOe'
    | 'invite_reviewGathering'
    | 'invite_reviewContent',
  param: {
    contentId?: number
    gathering?: number
    test?: boolean
  }
) => {
  const response = await useAxios().get('/my/nudge-modal', {
    params: {
      type,
      ...param,
    },
  })
  return response.data as {
    value: {
      image: {
        uri: string
        width: number
        height: number
      }
      title: {
        text: string
        font_size: number
        font_family: string
        font_color: string
        line_height: number
        letter_spacing: number
      }
      sub_title: {
        text: string
        font_size: number
        font_family: string
        font_color: string
        line_height: number
        letter_spacing: number
        highlight_text?: string
        highlight_color?: string
      }
      confirm_button: {
        text: string
        font_size: number
        font_family: string
        font_color: string
        background_color: string
      }
      cancel_button: {
        text: string
        font_size: number
        font_family: string
        font_color: string
        background_color: string
      }
    } | null
  }
}

const canIActivity = async (gatheringId: number) => {
  const response = await useAxios().get('/gathering/can-i-activity', {
    params: {
      gatheringId,
    },
  })
  const { result } = response.data as { result: boolean }
  return result
}

const getSubscriptionProductId = async (
  type: '모임' | '추가활동권',
  gatheringParam?: { regionCode: string; category: string }
) => {
  const response = await useAxios().get('/subscription/product-id/v2', {
    params: {
      type,
      ...gatheringParam,
    },
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message: '최대수량초과'
    }
    value?: {
      productId: string
      isPromotion: boolean
      isHalf: boolean
      chanceCount?: number
    }
  }
  return data
}

const getSubscriptionEventPeriod = async () => {
  const response = await useAxios().get('/subscription/event-period', {})
  const data = response.data as {
    period: {
      startAt: Date
      endAt: Date
    } | null
  }
  if (data.period) {
    data.period.startAt = new Date(data.period.startAt)
    data.period.endAt = new Date(data.period.endAt)
  }
  return data
}

const kakaoAuthentication = async (kakaoAccessToken: string, kakaoRefreshToken: string) => {
  const response = await useAxios().post('/auth/kakao/authentication', {
    kakaoAccessToken,
    kakaoRefreshToken,
  })
  const data = response.data as {
    value: {
      result: boolean
      fail?: {
        message: '토큰오류'
      }
      userState?: '신규' | '기존' | '탈퇴'
      newUser?: {
        tempToken: string
        phone: string
        nicknamePassed: boolean
        nickname: string
        image: null | string
        birthYear: number
        birthMonth: null | number
        birthDate: null | number
        gender: '남' | '여'
        agreeMarketingNoti: boolean
      }
      existingUser?: {
        accessToken: string
        refreshToken: string
        user: {
          id: number
          phone: string
          regionCode: string
          nickname: string
          image: null | string
          interestList: string[]
          birthYear: number
          birthday: null | Date
          gender: null | '남' | '여'
          unlockList: string[]
          ban: boolean
          profileUpdateChances: {
            countNickname: number
            countBirthday: number
            countGender: number
          }
        }
      }
      withdrawInfo?: {
        withdrawAt: Date
        unlockAt: Date
      }
    }
  }
  if (data.value.existingUser) {
    const { accessToken, refreshToken } = data.value.existingUser
    useAuthTokenStore().setAccessToken(accessToken)
    useAuthTokenStore().setRefreshToken(refreshToken)
  }
  return {
    ...data.value,
  }
}

const signupWithKakao = async (payload: {
  tempToken: string
  phone: string
  nickname: string
  image: string | null
  birthYear: number
  birthMonth: number | null
  birthDate: number | null
  interestList: string[]
  regionCode: string
  gender: '남' | '여'
  agreeMarketingNoti: boolean
}) => {
  const response = await useAxios().post('/auth/kakao/sign-up', {
    ...payload,
    source: 'web',
  })
  const data = response.data as {
    value: {
      result: boolean
      fail?: {
        message: 'temptoken오류' | '닉네임중복' | '부적절한닉네임' | '폰번호중복'
      }
      accessToken?: string
      refreshToken?: string
      user?: {
        id: number
        phone: string
        regionCode: string
        nickname: string
        image: null | string
        interestList: string[]
        birthYear: number
        birthday: null | Date
        gender: null | '남' | '여'
        unlockList: string[]
        ban: boolean
        profileUpdateChances: {
          countNickname: number
          countBirthday: number
          countGender: number
        }
      }
    }
  }
  return data.value
}

const recommendGatheringSchedule = async (category: string, regionCode: string) => {
  const response = await useAxios().get('/gathering/schedule/recommend', {
    params: {
      category,
      regionCode,
    },
  })
  const data = response.data as {
    value: RecommendedSchedule | null
  }
  if (data.value) {
    data.value.closingAt = new Date(data.value.closingAt)
    data.value.createdAt = new Date(data.value.createdAt)
    data.value.when = new Date(data.value.when)
  }
  return data.value
}

const checkNickname = async (nickname: string) => {
  const response = await useAxios().get('/auth/check/nickname', {
    params: {
      nickname,
    },
  })
  const data = response.data as {
    value: {
      result: boolean
    }
  }
  return data.value
}

const getCategoryList = async () => {
  const response = await useAxios().get('/category/list')
  const data = response.data as {
    list: {
      name: string
      totalCount: number
      subsCount: number
    }[]
  }
  return data.list
}

const addWaitingForGathering = async (category: string, regionCode: string) => {
  await useAxios().post('/gathering/add-waiting/web', {
    category,
    regionCode,
  })
}

const logAlimtalkClicked = async (userId: number, segmentId: number) => {
  await useAxios().post('/log/alimtalk-clicked', {
    userId,
    segmentId,
  })
}

const getGatheringInvitation = async (invitationId: number, inviteeId: number) => {
  const response = await useAxios().get('/gathering/invitation', {
    params: { invitationId, inviteeId },
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message: '없음' | '권한없음'
    }
    value?: {
      id: number
      message: string
      gathering: {
        id: number
        title: string
        image: string
        regionCode: string
        category: string
        memberCount: number
        requireBadge: boolean
      } | null
      inviteeId: number
      inviteeNickname: string // 받은사람
      inviterId: number
      inviterNickname: string // 보낸사람
    }
  }
  return data.value || null
}

const getAiRecommendSchedule = async (recommendId: number) => {
  const response = await useAxios().get('/gathering/schedule/ai-recommend', {
    params: { recommendId },
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: {
      id: number
      title: string
      description: string
      images: string[]
      links: {
        url: string
        title: string
        description: string
        image: string
      }[]
      feedbackType: null | 'like' | 'dislike'
    }
  }
  if (data.value) {
    data.value.links = data.value.links.filter((item) => !!item)
  }
  return data.value || null
}

const clickAiRecommendSchedule = async (recommendId: number) => {
  await useAxios().post('/gathering/schedule/ai-recommend/click-time', {
    recommendId,
  })
}

const feedbackAiRecommendSchedule = async (
  recommendId: number,
  feedbackType: 'like' | 'dislike',
  dislikeReason: string | null = null
) => {
  return useAxios().post('/gathering/schedule/ai-recommend/feedback', {
    recommendId,
    feedbackType,
    dislikeReason,
  })
}

const getProductHotdeal = async () => {
  const response = await useAxios().get('/product/hotdeal')
  const data = response.data as null | {
    value: null | {
      id: number
      name: string
      thumbnailImage: string
      originalPrice: number
      salesPrice: number
      discountRate: number
      saleStartAt: Date
      saleEndAt: Date
      remainingStock: number | null // 남은 재고 표시용
      createdAt: Date
    }
  }
  if (!data?.value) {
    return null
  }
  data.value.saleStartAt = new Date(data.value.saleStartAt)
  data.value.saleEndAt = new Date(data.value.saleEndAt)
  data.value.createdAt = new Date(data.value.createdAt)
  return data.value
}

const getProduct = async (id: number) => {
  const response = await useAxios().get('/product', { params: { id } })
  const { value } = response.data as {
    result: boolean
    fail?: {
      message: '없음'
    }
    value?: {
      id: number
      name: string
      thumbnailImage: string
      mainImage: string
      detailImages: string[]
      originalPrice: number
      salesPrice: number
      discountRate: number
      saleStartAt: Date
      saleEndAt: Date
      deliveryExpectedDate: string // YYYY-MM-DD
      deliveryAt: Date | null
      stock: number | null // 옵션이 없을때 -> null 무제한
      remainingStock: number | null // 남은 재고 (상품 총 재고)
      hasOption: boolean
      optionTitleName: string | null
      options:
        | {
            id: number
            name: string
            additionalPrice: number
            stock: number | null // null 무제한
          }[]
        | null
      shippingType: '무료' | '유료' | '조건 무료'
      shippingFee: number
      freeShippingMinOrderAmount: number | null // 조건 무료 시 무료 배송 최소 주문 금액
      shippingText: string
      videoUrls: string[]
      sellerName: string
      sellerContact: string
      sellerInfo: object
      productInfo: object
      shippingInfo: object
      createdAt: Date
    }
  }
  if (!value) {
    return null
  }
  value.saleStartAt = new Date(value.saleStartAt)
  value.saleEndAt = new Date(value.saleEndAt)
  value.createdAt = new Date(value.createdAt)
  if (value.deliveryAt) {
    value.deliveryAt = new Date(value.deliveryAt)
  }
  return value
}

const getPointBalance = async () => {
  const response = await useAxios().get('/point/balance')
  const data = response.data as {
    point: number
    expiringPoint: number
  }
  return data
}

const issueProductPaymentOrderId = async (
  productId: number,
  items: {
    productOptionId?: number
    quantity: number
  }[],
  pointUsedAmount: number,
  totalPaidAmount: number
) => {
  const response = await useAxios().post('/product/payment/orderId', {
    productId,
    items,
    pointUsedAmount,
    totalPaidAmount,
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message: '없음' | '판매마감' | '재고부족' | '결제금액오류' | '구매제한수량초과'
    }
    value?: {
      orderId: string
    }
  }
  return data
}

const productPaymentConfirm = async (
  paymentKey: string,
  orderId: string,
  productId: number,
  items: {
    productOptionId?: number
    quantity: number
  }[],
  pointUsedAmount: number,
  totalPaidAmount: number,
  delivery: {
    receiverName: string
    receiverPhone: string
    address: string
    addressDetail: string | null
    zipcode: string
    deliveryMessage: string | null
  },
  TosspaymentsTestCode?: string
) => {
  const response = await useAxios().post('/product/payment/confirm', {
    paymentKey,
    orderId,
    productId,
    items,
    pointUsedAmount,
    totalPaidAmount,
    delivery,
    TosspaymentsTestCode,
  })
  const data = response.data as {
    result: boolean
    fail?: {
      message:
        | '없음'
        | '판매마감'
        | '재고부족'
        | '결제금액오류'
        | '구매제한수량초과'
        | '임시데이터없음'
        | '임시데이터불일치'
        | '토스승인오류'
      tossErrorCode?: string
    }
  }
  return data
}

const getMyAddress = async () => {
  const response = await useAxios().get('/my/address')
  const data = response.data as {
    value: {
      receiverName: string
      receiverPhone: string
      address: string
      addressDetail: string | null
      zipcode: string
    } | null
  }
  return data.value
}

const setMyAddress = async (address: {
  receiverName: string
  receiverPhone: string
  address: string
  addressDetail: string | null
  zipcode: string
}) => {
  await useAxios().put('/my/address', address)
}

const getPointRatio = async (type: 'content' | 'product' | 'dining') => {
  const response = await useAxios().get('/point/ratio', { params: { type } })
  const data = response.data as { ratio: number }
  return 0.01 * data.ratio
}

const auth = {
  signupWithKakao: async (payload: {
    tempToken: string
    phone: string
    nickname: string
    image: string | null
    birthYear: number
    birthMonth: number | null
    birthDate: number | null
    interestList: string[]
    regionCode: string
    gender: '남' | '여'
    agreeMarketingNoti: boolean
  }) => {
    const response = await useAxios().post('/auth/kakao/sign-up', {
      ...payload,
      source: 'web',
    })
    const data = response.data as {
      value: {
        result: boolean
        fail?: {
          message: 'temptoken오류' | '닉네임중복' | '부적절한닉네임' | '폰번호중복'
        }
        accessToken?: string
        refreshToken?: string
        user?: {
          id: number
          phone: string
          regionCode: string
          nickname: string
          image: null | string
          interestList: string[]
          birthYear: number
          birthday: null | Date
          gender: null | '남' | '여'
          unlockList: string[]
          ban: boolean
          profileUpdateChances: {
            countNickname: number
            countBirthday: number
            countGender: number
          }
        }
      }
    }
    return data.value
  },
  getRandomNickname: async () => {
    const response = await useAxios().get('/auth/random-nickname')
    const data = response.data as {
      value: string
    }
    return data.value
  },
}

const dining = {
  // 2026.10.13 작업 2-10: 친구 초대 링크(referrerId)를 뺌 (친구 초대 10.02 폐지)
  getThisWeek: async (params?: { partnerLinkCode?: string }) => {
    const { partnerLinkCode } = params || {}
    const response = await useAxios().get('/dining/this-week', {
      params: { partnerLinkCode },
    })
    const data = response.data as {
      value: DiningThisWeek
    }
    if (data.value) {
      data.value.matchingStartAt = new Date(data.value.matchingStartAt)
      data.value.recruitStartAt = new Date(data.value.recruitStartAt)
      data.value.letterOpenAt = new Date(data.value.letterOpenAt)
      data.value.diningAt = new Date(data.value.diningAt)
    }
    return {
      dining: data.value,
    }
  },
  getDetail: async (orderId: string) => {
    const response = await useAxios().get('/dining', { params: { orderId } })
    const data = response.data as {
      result: boolean
      fail?: {
        message: '없음'
      }
      value?: DiningOrder
    }
    if (data.fail) {
      return null
    }
    if (data.value) {
      data.value.paidAt = new Date(data.value.paidAt)
      if (data.value.canceledAt) {
        data.value.canceledAt = new Date(data.value.canceledAt)
      }
      data.value.recruitStartAt = new Date(data.value.recruitStartAt)
      data.value.matchingStartAt = new Date(data.value.matchingStartAt)
      data.value.letterOpenAt = new Date(data.value.letterOpenAt)
      data.value.diningAt = new Date(data.value.diningAt)
      if (data.value.chat?.lastChat) {
        data.value.chat.lastChat.sendTime = new Date(data.value.chat.lastChat.sendTime)
      }
      // 재기획 10.02: 대기열 정보 날짜 변환 (서버는 문자열로 보냄)
      const w = data.value.wait
      if (w) {
        w.currentDiningAt = new Date(w.currentDiningAt)
        w.previousDiningAt = w.previousDiningAt ? new Date(w.previousDiningAt) : null
        w.resultAt = new Date(w.resultAt)
        w.autoRefundAt = new Date(w.autoRefundAt)
      }
    }
    return data.value!
  },
  getRegionList: async () => {
    const response = await useAxios().get('/dining/region/list')
    const data = response.data as {
      list: {
        region: string
        subRegions: {
          name: string
          isActive: boolean
        }[]
      }[]
    }
    return data.list
  },
  /**
   * 신청 화면 '자리 현황'. 세부지역마다 로그인한 신청자 기준 테이블 상태를 받는다 (재기획 09.30)
   *
   * 서버: oe-api 24.15 (2026.10.13 작업 2-4). 매칭 확률(작업 2-5)·home(작업 2-6)까지 서버가 채움
   * @returns list: 세부지역별 테이블 상태 / home: 가입 때 고른 지역에 맞는 다이닝 세부지역 (첫 화면 '내 지역' 카드)
   */
  getMyRegionStatus: async (): Promise<DiningRegionMyStatusResponse> => {
    const response = await useAxios().get('/dining/region/my-status')
    return response.data as DiningRegionMyStatusResponse
  },
  /**
   * 이번 주 건너뛰기 — 결제는 그대로 두고 다음 주 수요일 매칭으로 미룬다 (재기획 10.02)
   * NOTE(정책) 10.02 대표님: 건너뛰면 우선순위가 처음으로 돌아감 (wait.priority = false)
   * NOTE(정책) 화요일 오전 10시 확정 전에만 가능. 확정 뒤에는 서버가 거절 ('확정됨')
   * 서버: oe-api 24.14 (2026.10.13 작업 2-2). 신청의 회차를 다음 주로 옮기고 미룬 시각·횟수를 적음
   * @returns 성공하면 value에 옮겨 간 회차와 다이닝 시각, 실패하면 fail.message에 사유
   */
  skipThisWeek: async (orderId: string, diningId?: number) => {
    // diningId: 화면이 보고 있던 신청의 회차. 서버가 지금 회차와 다르면 '이미미룸'으로 거절 (2026.10.15 두 번 누르기 방지)
    const response = await useAxios().post('/dining/order/skip-week', { orderId, diningId })
    return response.data as {
      result: boolean
      fail?: {
        // 없음: 내 신청이 아님 / 취소된신청 / 확정됨: 화 10시가 지남 / 이미결제: 다음 회차에 이미 결제가 있음 / 이미미룸: 이미 다음 주로 옮겨 감
        message: '없음' | '취소된신청' | '확정됨' | '확정중' | '이미결제' | '이미미룸'
      }
      value?: { diningId: number; diningAt: string }
    }
  },
  waitRegion: async (region: string, subRegion: string) => {
    return useAxios().post('/dining/region/wait', {
      region,
      subRegion,
    })
  },
  getOrderList: async () => {
    const response = await useAxios().get('/dining/order/list')
    const data = response.data as {
      list: {
        orderId: string
        status: '결제완료' | '결제취소'
        paidAt: Date
        canceledAt: Date | null
        totalPaidAmount: number
        profileImage: string | null
        dining: {
          id: number
          recruitStartAt: Date
          matchingStartAt: Date
          letterOpenAt: Date
          diningAt: Date
          memberCount: number | null
          progressPercent: number
          groupId: number | null
        }
        /*
          재기획 10.02 대기열 방식: 참가 내역 목록에 상태를 보여주려고 추가 (pages/app/dining/order/list.vue)
          서버: oe-api 24.8 (2026.10.13 작업 2-7). dining(회차)은 이 신청이 지금 들어 있는 회차 (한 주 미루기·이어감이면 옮겨 간 회차)
        */
        /** 취소 이유. '기간만료' = 12주 동안 안 정해져 자동 환불 / '지역닫힘' = 운영이 지역을 닫아 환불 (stores/dining.ts DiningOrder.cancelReason과 같음) */
        cancelReason?: '매칭실패' | '기간만료' | '지역닫힘' | null
        /** 대기열 상태 요약. 신청 상세 wait의 일부만 */
        wait?: {
          state: 'waiting' | 'carried' | 'confirmed' | 'expired'
          /** 지금 노리는 수요일 19:00 */
          currentDiningAt: Date
          /** 이번 주 매칭 확률 (0~100). 매칭 중일 때만 */
          matchProbability: number | null
        } | null
        /** 후기를 남기면 줄 포인트. 이미 남겼거나 기간이 지났으면 null (10.02 대표님: 후기마다 1,000P) */
        feedbackPoint?: number | null
      }[]
    }
    return data.list.map((item) => {
      if (item.wait) item.wait.currentDiningAt = new Date(item.wait.currentDiningAt)
      item.paidAt = new Date(item.paidAt)
      if (item.canceledAt) {
        item.canceledAt = new Date(item.canceledAt)
      }
      item.dining.recruitStartAt = new Date(item.dining.recruitStartAt)
      item.dining.matchingStartAt = new Date(item.dining.matchingStartAt)
      item.dining.letterOpenAt = new Date(item.dining.letterOpenAt)
      item.dining.diningAt = new Date(item.dining.diningAt)
      return item
    })
  },
  issueOrderId: async (
    diningId: number,
    pointUsedAmount: number,
    totalPaidAmount: number,
    areaRegion: string,
    areaSubRegion: string,
    discountParams?: {
      partnerLinkCode?: string
    }
  ) => {
    // 재기획 10.02: 대기열 방식은 날짜를 고르지 않음 (이번 주부터 정해질 때까지 자동) → 보내는 값은 기존과 같음
    // NOTE(정책) 10.07 대표님 확정: 진행 중인 신청(매칭 중·확정 ~ 다이닝 끝)이 있으면 서버가 '진행중인신청'으로 거절하고 그 주문번호를 줌
    //           (oe-api getActiveDiningOrder, 2026.10.13 작업 2-1). 결제 확정(confirm)에서도 한 번 더 확인. 화면 처리는 utils/dining-active-order.ts
    // 2026.10.13 작업 2-10: 친구 초대 0원 결제(parentOrderId)를 뺌 — 서버도 '초대종료'로 거절
    const { partnerLinkCode } = discountParams || {}
    const response = await useAxios().post('/dining/payment/orderId', {
      diningId,
      pointUsedAmount,
      totalPaidAmount,
      areaRegion,
      areaSubRegion,
      partnerLinkCode,
    })
    const data = response.data as {
      result: boolean
      fail?: {
        message:
          | '없음'
          | '판매마감'
          | '결제금액오류'
          | '지역오류'
          | '수락된초대'
          | '이미결제'
          | '파트너링크없음'
          | '진행중인신청'
          | '다이닝정지' // 2026.10.14 노쇼 제재 중 → 정지 안내 화면
        // '진행중인신청'일 때만: 진행 중인 신청의 주문번호
        orderId?: string
      }
      value?: {
        orderId: string
      }
    }
    return data
  },
  paymentConfirm: async (
    paymentKey: string,
    orderId: string,
    diningId: number,
    pointUsedAmount: number,
    totalPaidAmount: number,
    areaRegion: string,
    areaSubRegion: string,
    discountParams?: {
      partnerLinkCode?: string
    },
    TosspaymentsTestCode?: string
  ) => {
    // 2026.10.13 작업 2-10: 친구 초대 0원 결제(parentOrderId)를 뺌 — 서버도 '초대종료'로 거절
    const { partnerLinkCode } = discountParams || {}
    const response = await useAxios().post('/dining/payment/confirm', {
      paymentKey,
      orderId,
      diningId,
      pointUsedAmount,
      totalPaidAmount,
      areaRegion,
      areaSubRegion,
      TosspaymentsTestCode,
      partnerLinkCode,
    })
    const data = response.data as {
      result: boolean
      fail?: {
        message:
          | '없음'
          | '판매마감'
          | '결제금액오류'
          | '지역오류'
          | '수락된초대'
          | '이미결제'
          | '파트너링크없음'
          | '임시데이터없음'
          | '임시데이터불일치'
          | '토스승인오류'
          | '진행중인신청'
          | '다이닝정지' // 2026.10.14 노쇼 제재 중 → 정지 안내 화면
        tossErrorCode?: string
        // '진행중인신청'일 때만: 진행 중인 신청의 주문번호
        orderId?: string
      }
    }
    return data
  },
  paymentCancel: async (orderId: string, idempotencyKey: string, TosspaymentsTestCode?: string) => {
    const response = await useAxios().post('/dining/payment/cancel', {
      orderId,
      idempotencyKey,
      TosspaymentsTestCode,
    })
    const data = response.data as {
      result: boolean
      fail?: {
        message: '토스취소오류' | '결제정보없음' | '이미취소됨' | '확정됨' | '확정중' // 화 10시 뒤: 조에 들면 확정됨, 아직 조가 없으면 확정중 (10.14)
        tossErrorCode?: string
      }
    }
    return data
  },
  // 2026.10.13 작업 2-10: getInvitationInfo(친구 초대 정보, 24.10)를 지움 — 친구 초대 10.02 폐지, 서버도 '없음'으로 응답

  canWriteFeedback: async (orderId: string, diningGroupId: number) => {
    const response = await useAxios().get('/dining/can-write-feedback', {
      params: { orderId, diningGroupId },
    })
    const data = response.data as {
      result: boolean
      fail?: {
        message: '없음' | '권한없음' | '이미작성됨' | '피드백마감'
      }
    }
    return data
  },

  feedback: async (
    diningId: number,
    feedback: {
      isRestaurntGood: {
        value: boolean
        goodReasons?: (
          | '음식이 맛있어요'
          | '직원이 친절해요'
          | '위치 찾기 쉬워요'
          | '주차가 편해요'
          | '분위기가 좋아요'
          | '가격이 적당해요'
        )[]
        badReasons?: (
          | '음식이 별로였어요'
          | '직원이 불친절했어요'
          | '위치 찾기 어려웠어요'
          | '주차가 불편했어요'
          | '대화하기 시끄러웠어요'
          | '가격이 비쌌어요'
          | string // 기타
        )[]
      }
      isDiningGood: {
        value: boolean
        goodReasons?: (
          | '대화가 잘 통했어요'
          | '서로 매너가 좋았어요'
          | '멤버 구성이 좋았어요'
          | '신선하고 특별한 경험이었어요'
          | '다음에도 참여하고 싶어요'
        )[]
        bad?: {
          어색: boolean
          멤버구성: {
            isBad: boolean
            badReasons?: (
              | '나이 차이가 컸어요'
              | '관심사가 맞지 않았어요'
              | '성비가 맞지 않았어요'
            )[]
          }
          불편한사람: {
            value: boolean
            users: {
              id: number
              reasons: (
                | '매너/태도가 불편했어요'
                | '음주가 과했어요'
                | '의견 충돌/언쟁 분위기였어요'
                | '사적인 질문이 부담됐어요'
                | '폰만 보거나 집중을 안 했어요'
              )[]
            }[]
          }
          노쇼: {
            value: boolean
            userIds: number[]
          }
          지각: {
            value: boolean
            userIds: number[]
          }
          정산불편: boolean
          기타?: string
        }
      }
    }
  ) => {
    // 2026.10.13 작업 2-8: 서버가 이번 후기로 지급한 포인트를 돌려줌 (1000 / 안 줬으면 0). 예전 서버는 빈 응답
    const response = await useAxios().post('/dining/feedback', {
      diningId,
      feedback,
    })
    // 개발자분 답변(10.13): 쓸 수 없는 후기는 서버도 거절 → result false + fail.message (24.11 can-write-feedback과 같은 문구)
    return response.data as {
      result?: boolean
      fail?: { message: '권한없음' | '이미작성됨' | '피드백마감' }
      rewardPoint?: number
    }
  },

  bannedCheck: async () => {
    const response = await useAxios().get('/dining/banned-check')
    const data = response.data as {
      value: DiningBanInfo
    }
    if (data.value) {
      data.value.diningAt = new Date(data.value.diningAt)
      data.value.startAt = new Date(data.value.startAt)
      data.value.until = new Date(data.value.until)
    }
    return {
      diningBanInfo: data.value || null,
    }
  },
}

const log = {
  paymentIssue: async (orderId: string, text: string) => {
    return useAxios().post('/log/payment/issue', {
      orderId,
      text,
    })
  },
}

const metaConversion = async (eventName: string, eventId: string, params?: Record<string, any>) => {
  return useAxios().post('/meta-conversion', {
    eventName,
    eventId,
    params: params || {},
  })
}

const getRandomProfileImages = async () => {
  return useAxios().get('/random-profile-images?limit=100')
}

const getIdentityInfo = async () => {
  const response = await useAxios().get('/my/profile/user-identity-info')
  const data = response.data as {
    value: IdentityInfo
  }
  return {
    identityInfo: data.value || null,
  }
}

const KGIdentityVerified = async (identityVerificationId: string) => {
  const response = await useAxios().post('/auth/kg-identity-verified', {
    identityVerificationId,
  })
  return response.data as { result: boolean }
}

export default {
  getGatheringInfo,
  getAllGatheringIds,
  getEmoticonInfo,
  getEmoticonStoreList,
  getGatheringList,
  getGatheringFeedList,
  getGatheringFeedDetail,
  getContent,
  getContentV2,
  getContentForPublic,
  getAccessToken,
  getUntransferredOEId,
  getContentWishUserList,
  getContentWishUserListForPublic,
  toggleContentReviewLike,
  toggleContentWish,
  getContentReviewList,
  getContentReviewListForPublic,
  cancelContent,
  getContentMemberList,
  visitContent,
  getChatHealth,
  requestMyReferrerLink,
  getMyNudgeModal,
  canIActivity,
  getSubscriptionProductId,
  getSubscriptionEventPeriod,
  kakaoAuthentication,
  recommendGatheringSchedule,
  checkNickname,
  signupWithKakao,
  getCategoryList,
  addWaitingForGathering,
  logAlimtalkClicked,
  getGatheringInvitation,
  getAiRecommendSchedule,
  clickAiRecommendSchedule,
  feedbackAiRecommendSchedule,
  getProductHotdeal,
  getProduct,
  getPointBalance,
  issueProductPaymentOrderId,
  productPaymentConfirm,
  getMyAddress,
  setMyAddress,
  getPointRatio,
  dining,
  log,
  auth,
  metaConversion,
  getRandomProfileImages,
  getIdentityInfo,
  KGIdentityVerified,
}
